/**
 * Job line draft state shared by JobLineEditor, JobCreatePage and
 * JobDetailPage, plus the mapping to and from the API.
 *
 * Pricing rule (backend JobService): a unitPrice in the request (0 included)
 * makes the line MANUAL. Without one, a new line is priced automatically
 * (CHART from the item's price chart at the property's size, else CATALOG),
 * and an edited line keeps its stored price and source unless its item
 * changes. So the draft's unitPrice is only an override, never a copy of an
 * automatic price.
 */

import * as types from '../api/types';
import { getErrorCode, getErrorMessage } from '../utils/apiError';

export interface LineDraft {
  key: string;
  itemId: string;
  itemName: string;
  quantity: string;
  pricingModel: types.JobPricingModel;
  /** Manual price override; '' = automatic pricing. */
  unitPrice: string;
  /**
   * Edit mode only: the saved line's item and price source. While the item
   * is unchanged and no override is entered, the backend keeps the stored
   * price, so no fresh quote applies. Absent on new lines (and on drafts
   * saved to sessionStorage before these fields existed).
   */
  originalItemId?: string;
  originalPriceSource?: types.JobPriceSource;
}

export function newLineDraft(): LineDraft {
  return { key: `${Date.now()}-${Math.random()}`, itemId: '', itemName: '', quantity: '1', pricingModel: 'PER_ITEM', unitPrice: '' };
}

/**
 * Draft for editing a saved line. Only MANUAL lines preload their price into
 * the override: preloading a CHART/CATALOG price would send it back as a
 * unitPrice and silently turn the line MANUAL.
 */
export function lineDraftFromLine(line: types.JobLineItem): LineDraft {
  const source = line.priceSource ?? 'CATALOG';
  return {
    key: line.id,
    itemId: line.itemId,
    itemName: line.item.name,
    quantity: line.quantity,
    pricingModel: line.pricingModel,
    unitPrice: source === 'MANUAL' ? line.unitPrice : '',
    originalItemId: line.itemId,
    originalPriceSource: source,
  };
}

export function validateLineDraft(line: LineDraft): string | null {
  if (!line.itemId) return 'Choose a catalog item';
  if (!Number.isFinite(Number(line.quantity)) || Number(line.quantity) <= 0) return 'Quantity must be greater than zero';
  if (line.pricingModel === 'FIXED' && Number(line.quantity) !== 1) return 'Fixed pricing requires quantity 1';
  if (line.unitPrice !== '' && (!Number.isFinite(Number(line.unitPrice)) || Number(line.unitPrice) < 0)) return 'Price must be zero or greater';
  return null;
}

/** Request body; unitPrice is sent only when an override is entered. */
export function linePayload(line: LineDraft): types.AddJobLineItemRequest {
  return {
    itemId: line.itemId,
    quantity: line.quantity,
    pricingModel: line.pricingModel,
    ...(line.unitPrice !== '' ? { unitPrice: line.unitPrice } : {}),
  };
}

/**
 * Whether the editor should ask the backend for a fresh price: a new line, or
 * an edited line whose item changed (the backend re-prices only then).
 */
export function needsPriceQuote(line: Pick<LineDraft, 'itemId' | 'originalItemId'>, propertyId: string | null | undefined): boolean {
  return Boolean(propertyId && line.itemId && (!line.originalItemId || line.itemId !== line.originalItemId));
}

/** True while an edited line keeps its stored price (same item, no override). */
export function keepsStoredPrice(line: Pick<LineDraft, 'itemId' | 'originalItemId' | 'unitPrice'>): boolean {
  return Boolean(line.originalItemId && line.itemId === line.originalItemId && line.unitPrice === '');
}

export type QuoteIssueTone = 'warning' | 'error';

export interface QuoteIssue {
  tone: QuoteIssueTone;
  code: string | null;
  message: string;
  /** Show a link to the property's edit page (the fix is setting/changing its size). */
  linkToProperty: boolean;
}

/** Map a failed POST /pricing/quote to what the job line editor shows. */
export function describeQuoteError(err: unknown): QuoteIssue {
  const code = getErrorCode(err) ?? null;
  switch (code) {
    case 'PROPERTY_SIZE_REQUIRED':
      return {
        tone: 'warning',
        code,
        message:
          "This item is priced from a price chart, but the property has no size. Set the property's size, or enter a unit price override.",
        linkToProperty: true,
      };
    case 'UNIT_MISMATCH':
      return {
        tone: 'error',
        code,
        message:
          "The item's price chart and the property's size use different kinds of units (area vs. length). Change the property's size unit, or enter a unit price override.",
        linkToProperty: true,
      };
    case 'PRICE_CHART_INACTIVE':
      return {
        tone: 'error',
        code,
        message:
          "The item's price chart is inactive. Activate it in Settings > Price Charts, unlink it from the item, or enter a unit price override.",
        linkToProperty: false,
      };
    case 'PRICE_CHART_NO_TIERS':
      return {
        tone: 'error',
        code,
        message: "The item's price chart has no bands yet. Add bands in Settings > Price Charts, or enter a unit price override.",
        linkToProperty: false,
      };
    default:
      return { tone: 'error', code, message: getErrorMessage(err, 'Could not preview the price'), linkToProperty: false };
  }
}
